import React, { useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import classNames from 'classnames';
import { Modal } from '@/components';
import { ReportModalProps } from './report-modal.types';
import '@/assets/styles/components/report-modal.styles.scss';
import 'swiper/css';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { AssessmentTypeResponse } from '@dopingtech/live_expert_bff_api_ts';
import { InteractionAssessmentReportCreateRequest, InteractionAssessmentReportCreateRequestAssessmentTypeEnum as ReportTypes } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { toast } from 'react-toastify';
import { TimeSpan } from '@/utility/time-span.ts';

const MIN_DESCRIPTION_LENGTH = 50;

const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, studentId, interactionId }) => {
	const [activeSlide, setActiveSlide] = useState(0);
	const [selectedType, setSelectedType] = useState<AssessmentTypeResponse | null>(null);
	const [selectedCrisisType, setSelectedCrisisType] = useState<AssessmentTypeResponse | null>(null);
	const [description, setDescription] = useState('');
	const [preventFocus, setPreventFocus] = useState(false);

	const swiperRef = useRef<SwiperType>();

	const { FeedbackControllerApi } = useApi();

	const { data: mainOptions } = useQueryProvider([QUERY_KEYS.findAssessmentTypes], () => FeedbackControllerApi.findAssessmentTypes(), {
		refetchOnWindowFocus: false,
		select: ({ data }) => data.data
	});

	const { data: subOptions } = useQueryProvider([QUERY_KEYS.findAssessmentTypes, selectedType?.description ?? ''], () => FeedbackControllerApi.findAssessmentTypes(selectedType?.id), {
		select: ({ data }) => data.data,
		enabled: !!selectedType && !selectedType?.reportWithMessage && activeSlide === 1
	});

	const saveReport = useMutationProvider((body: InteractionAssessmentReportCreateRequest) => FeedbackControllerApi.createAssessmentReport(body), {
		onSuccess: () => {
			toast.success('Rapor başarıyla gönderildi');
		}
	});

	const resetModal = () => {
		setActiveSlide(0);
		setSelectedType(null);
		setSelectedCrisisType(null);
		setDescription('');
		swiperRef.current?.slideTo(0);
	};

	const handleClose = () => {
		resetModal();
		onClose();
	};

	const handleTypeSelect = (type: AssessmentTypeResponse) => {
		setSelectedType(type);
		setSelectedCrisisType(null);
		setDescription('');
	};

	const handleCrisisTypeSelect = (type: AssessmentTypeResponse) => {
		setSelectedCrisisType(type);
		setDescription('');
	};

	const handleBack = () => {
		if (activeSlide === 0) {
			handleClose();
		} else {
			swiperRef.current?.slidePrev();
			setSelectedCrisisType(null);
		}
	};

	const handleSubmit = async () => {
		if (selectedType?.name) {
			const body: InteractionAssessmentReportCreateRequest = {
				assessmentType: (selectedType.name === ReportTypes.CrisisReport && selectedCrisisType?.name ? selectedCrisisType?.name : selectedType?.name) as ReportTypes,
				reportMessage: description,
				interactionId: interactionId,
				studentId: studentId
			};

			await saveReport.mutateAsync(body);
			handleClose();
		}
	};

	const isDescriptionValid = () => {
		return description.trim().length >= MIN_DESCRIPTION_LENGTH;
	};

	const handleNext = () => {
		if (selectedType) {
			setDescription('');
			swiperRef.current?.slideNext();
		}
	};

	const handleTextareaBlur = () => {
		setPreventFocus(true);

		setTimeout(() => {
			setPreventFocus(false);
		}, TimeSpan.milliSecond.threeHundredFifty);
	};

	const handleTextareaFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
		if (preventFocus) {
			e.target.blur();
			return;
		}
	};

	const handleTextareaClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
		if (preventFocus) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			closeOnEsc={true}
			closeOnClickOutside={true}
			size="sm"
			className="report-modal">
			<div
				className="close-icon"
				onClick={handleClose}>
				<i className="icons-close" />
			</div>
			<Swiper
				onSwiper={swiper => {
					swiperRef.current = swiper;
				}}
				onSlideChange={swiper => setActiveSlide(swiper.activeIndex)}
				allowTouchMove={false}
				className="report-swiper">
				<SwiperSlide>
					<div className="report-type-selection">
						<div className="report-modal-header">
							<div className="header-title">
								<i className="icons-danger-circle" />
								<h2>Raporla</h2>
							</div>
							<div className="header-description">
								<p>Hangi konuda raporlama yapmak istiyorsunuz?</p>
							</div>
						</div>

						<div className="report-options">
							{mainOptions?.map(type => (
								<label
									className={classNames('report-option', {
										selected: selectedType?.id === type?.id
									})}
									key={type?.id}>
									<input
										type="radio"
										name="reportType"
										value={type.id}
										checked={selectedType?.id === type?.id}
										onChange={() => handleTypeSelect(type)}
									/>
									<span className="radio-button"></span>
									{type?.description}
								</label>
							))}
						</div>

						<div className="button-group">
							<div
								className="btn secondary"
								onClick={handleBack}>
								Vazgeç
							</div>
							<div
								className={classNames('btn primary', {
									disabled: !selectedType
								})}
								onClick={handleNext}>
								İleri
								<i className="icons-arrow-right" />
							</div>
						</div>
					</div>
				</SwiperSlide>

				<SwiperSlide>
					{!selectedType?.reportWithMessage ? (
						<div className="report-type-selection">
							<div className="report-modal-header">
								<div className="header-title">
									<i className="icons-danger-circle" />
									<h2>Raporla</h2>
								</div>
								<div className="header-description">
									<p>Hangi konuyla ilgili kriz bildirmek istiyorsunuz?</p>
								</div>
							</div>

							<div className="report-options">
								{subOptions?.map(type => (
									<label
										className={classNames('report-option', {
											selected: selectedCrisisType?.id === type?.id
										})}
										key={type?.id}>
										<input
											type="radio"
											name="crisisType"
											value={type?.id}
											checked={selectedCrisisType?.id === type?.id}
											onChange={() => handleCrisisTypeSelect(type)}
										/>
										<span className="radio-button"></span>
										{type?.description}
									</label>
								))}
							</div>

							<div className="button-group">
								<div
									className="btn secondary"
									onClick={handleBack}>
									<i className="icons-arrow-left" />
									Geri
								</div>
								<div
									className={classNames('btn primary', {
										disabled: !selectedCrisisType
									})}
									onClick={() => handleSubmit()}>
									Raporla
								</div>
							</div>
						</div>
					) : (
						<div className="report-description">
							<div className="report-modal-header">
								<div className="header-title">
									<i className="icons-danger-circle" />
									<h2>Raporla</h2>
								</div>
								<div className="header-description">
									<p>Doping Hafıza hakkında raporlamak istediğiniz konuyu açıklayın.</p>
								</div>
							</div>

							<div className="form-group">
								<textarea
									value={description}
									onChange={e => setDescription(e.target.value)}
									placeholder="Konuyu açıklayın"
									rows={5}
									onBlurCapture={handleTextareaBlur}
									onFocus={handleTextareaFocus}
									onClick={handleTextareaClick}
									className="report-textarea"
								/>
							</div>

							<div className="button-group">
								<div
									className="btn secondary"
									onClick={handleBack}>
									<i className="icons-arrow-left" />
									Geri
								</div>
								<div
									className={classNames('btn primary', {
										disabled: !isDescriptionValid()
									})}
									onClick={() => isDescriptionValid() && handleSubmit()}>
									Raporla
								</div>
							</div>
						</div>
					)}
				</SwiperSlide>

				<SwiperSlide>
					{selectedType?.name === ReportTypes.CrisisReport && (
						<div className="report-description">
							<div className="report-modal-header">
								<div className="header-title">
									<i className="icons-danger-circle" />
									<h2>Kriz Bildirimi</h2>
								</div>
								<div className="header-description">
									<p>Lütfen kriz durumunu detaylı açıklayınız</p>
								</div>
							</div>

							<div className="form-group">
								<textarea
									value={description}
									onChange={e => setDescription(e.target.value)}
									placeholder="Kriz durumunu açıklayın"
									rows={5}
									onBlurCapture={handleTextareaBlur}
									onFocus={handleTextareaFocus}
									onClick={handleTextareaClick}
									className="report-textarea"
								/>
							</div>

							<div className="button-group">
								<div
									className="btn secondary"
									onClick={handleBack}>
									<i className="icons-arrow-left" />
									Geri
								</div>
								<div
									className={classNames('btn primary', {
										disabled: !isDescriptionValid()
									})}
									onClick={() => isDescriptionValid() && handleSubmit()}>
									Raporla
								</div>
							</div>
						</div>
					)}
				</SwiperSlide>
			</Swiper>
		</Modal>
	);
};

export default ReportModal;
